import api, { type ApiResponse } from "./api";

/** Bootstrap / display fallback only — live charge always comes from membership_plans. */
export const DEFAULT_PUBLISHER_MEMBERSHIP_PRICE_INR = 999;

/** Whole-rupee display limit that still fits the frozen blue panel at minimum typography. */
export const MAX_PUBLISHER_MEMBERSHIP_PRICE_INR = 99_999;

export type PublicMembershipPlan = {
  id: string;
  name: string;
  slug: string;
  price_amount: string | number;
  currency: string;
  is_per_app?: boolean;
};

export type PublicMembershipPrice = {
  amountInr: number;
  amountDisplay: string;
  currency: string;
  planId: string | null;
  slug: string | null;
};

type PlansResponse = {
  plans: PublicMembershipPlan[];
};

export function normalizeWholeRupeeAmount(raw: string | number): number | null {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || raw <= 0) {
      return null;
    }
    if (!Number.isInteger(raw)) {
      return null;
    }
    return raw;
  }

  const trimmed = String(raw).trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value <= 0) {
    return null;
  }
  return value;
}

export function validatePublisherMembershipPriceInput(raw: string): {
  ok: boolean;
  value: number | null;
  message: string;
} {
  const value = normalizeWholeRupeeAmount(raw);
  if (value === null) {
    return {
      ok: false,
      value: null,
      message: "Enter a valid whole-rupee amount greater than zero.",
    };
  }
  if (value > MAX_PUBLISHER_MEMBERSHIP_PRICE_INR) {
    return {
      ok: false,
      value: null,
      message: `Price must be ₹${MAX_PUBLISHER_MEMBERSHIP_PRICE_INR.toLocaleString("en-IN")} or less so it fits the Membership panel.`,
    };
  }
  return { ok: true, value, message: "" };
}

export function membershipPriceDigitCount(amountInr: number): number {
  return String(Math.trunc(Math.abs(amountInr))).length;
}

/** Controlled typography bucket for ₹ + amount only. Period text stays frozen. */
export function membershipPriceDigitsDataAttribute(amountInr: number): "3" | "4" | "5" {
  const digits = membershipPriceDigitCount(amountInr);
  if (digits >= 5) {
    return "5";
  }
  if (digits === 4) {
    return "4";
  }
  return "3";
}

export function pickAppAnnualPlan(
  plans: PublicMembershipPlan[],
): PublicMembershipPlan | null {
  return (
    plans.find((plan) => plan.slug === "app-annual") ??
    plans.find((plan) => plan.is_per_app) ??
    plans[0] ??
    null
  );
}

export async function fetchPublicPublisherMembershipPrice(): Promise<PublicMembershipPrice> {
  const response = await api.get<ApiResponse<PlansResponse>>("/membership/plans");
  const plan = pickAppAnnualPlan(response.data.data.plans ?? []);
  const amount =
    plan != null ? normalizeWholeRupeeAmount(Number(plan.price_amount)) : null;

  if (plan && amount != null) {
    return {
      amountInr: amount,
      amountDisplay: String(amount),
      currency: plan.currency || "INR",
      planId: plan.id,
      slug: plan.slug,
    };
  }

  return {
    amountInr: DEFAULT_PUBLISHER_MEMBERSHIP_PRICE_INR,
    amountDisplay: String(DEFAULT_PUBLISHER_MEMBERSHIP_PRICE_INR),
    currency: "INR",
    planId: plan?.id ?? null,
    slug: plan?.slug ?? "app-annual",
  };
}
